import {ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {
	distinctUntilChanged,
	finalize, forkJoin,
	Observable,
	of,
	Subscription,
	switchMap,
	takeWhile,
	tap
} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import {CriticalityLevel, TenantService} from 'src/app/services/tenant.service';
import {TranslateService} from '@ngx-translate/core';
import {SnackbarService} from 'src/app/services/front/snackbar.service';
import {FormControl} from '@angular/forms';
import {TenantAccountService} from 'src/app/services/back/tenant-account.service';
import {
	Server,
	ServerInformationForm,
	ServerInterfaceNetwork, ServerInterfaceNetworkDeleteForm, ServerInterfaceNetworkForm,
	ServerOS,
	ServerType
} from "../../../../../../../services/model/server.model";
import {ServerDetailData, ServerDetailService} from "../../../../../../../services/front/server-detail.service";
import {ServerService} from "../../../../../../../services/back/server.service";
import {ipAddressValidator, isNetworkConfigurationValid} from "../../../../../../../utils/forms.utils";
import {IpCheckerService} from "../../../../../../../services/front/ip-checker.service";

@Component({
	selector: 'app-server-information-form',
	standalone: false,
	templateUrl: './server-information-form.component.html',
	styleUrls: ['./server-information-form.component.scss']
})
export class ServerInformationFormComponent implements OnInit, OnDestroy {
	tenantId: string;
	isEditor: boolean;
	server: Server;

	_initializing: boolean;
	_refreshing: boolean;
	_loading: boolean;
	_saving: boolean;
	initialized: boolean = false;

	advancementPercent: number = 0;

	typeControl: FormControl<ServerType>;
	typeList: ServerType[] = [];

	systemControl: FormControl<ServerOS>;
	systemList: ServerOS[] = [];

	ipAddressControl: FormControl<string | null>;

	locationControl: FormControl<string | null>;
	locationSearchControl: FormControl<string | null>;

	locationList: string[] = [];
	filteredLocationList: string[] = [];

	manufacturerControl: FormControl<string | null>;

	criticalityControl: FormControl<CriticalityLevel | null>;

	subscription = new Subscription();

	constructor(private serverDetailService: ServerDetailService,
				private serverService: ServerService,
				private tenantAccountService: TenantAccountService,
				private translateService: TranslateService,
				private snackBarService: SnackbarService,
				private tenantService: TenantService,
				private ipCheckerService: IpCheckerService,
				private cdr: ChangeDetectorRef) {
	}

	ngOnInit() {
		this.createForms();
		this.subscription.add(this.serverDetailService.getInitializingChanges()
			.subscribe(initializing => this._initializing = initializing));
		this.subscription.add(this.serverDetailService.getRefreshingChanges()
			.subscribe(refreshing => this._refreshing = refreshing));
		this.subscription.add(this.serverDetailService.getServerDetailDataChanges()
			.pipe(tap(data => this.setServerDetailData(data)))
			.subscribe(() => this.initialize()));
	}

	private setServerDetailData(data: ServerDetailData): void {
		this.tenantId = data.tenantId;
		this.isEditor = data.isEditor;
		this.server = data.server;
	}

	initialize(): void {
		this.subscription.add(this.switchLoading()
			.pipe(
				switchMap(() => forkJoin([
					this.serverService.getServerCriticality(this.tenantId, this.server.id),
					this.serverService.getInfrastructureLocationList(this.tenantId)
				])),
				tap(([criticality, locations]) => this.setDefaultData(criticality, locations)),
				finalize(() => this.switchLoading()))
			.subscribe(() => this.initialized = true));
	}

	private createForms(): void {
		this.ipAddressControl = new FormControl(null, [ipAddressValidator()]);
		this.locationControl = new FormControl(null);
		this.locationSearchControl = new FormControl('');

		this.subscription.add(this.locationSearchControl.valueChanges.pipe(distinctUntilChanged())
			.subscribe(text => this.filterLocationList(text)));

		this.manufacturerControl = new FormControl(null);
		this.typeControl = new FormControl();
		this.systemControl = new FormControl();
		this.criticalityControl = new FormControl(null);

		this.typeList = Object.values(ServerType);
		this.systemList = Object.values(ServerOS);

		this.subscription.add(this.systemControl.valueChanges.pipe(distinctUntilChanged())
			.subscribe(() => this.updateServerInformation()));

		this.subscription.add(this.typeControl.valueChanges.pipe(distinctUntilChanged())
			.subscribe(() => this.updateServerInformation()));

		this.subscription.add(this.locationControl.valueChanges.pipe(distinctUntilChanged())
			.subscribe(() => this.updateServerInformation()));
	}

	filterLocationList(text?: string | null) {
		this.filteredLocationList = this.locationList
			.filter(value => !text || value.toLowerCase().includes(text.toLowerCase()))
			.sort((a, b) => a.localeCompare(b));
	}

	setDefaultData(criticality: CriticalityLevel | null, locations: string[]): void {
		this.criticalityControl.setValue(criticality, {emitEvent: false});

		this.locationList = locations;
		this.filteredLocationList = locations;
		this.locationControl.setValue(this.server.location, {emitEvent: false});

		const values: any[] = [this.getServerPrimaryInterface(this.server)?.serverInterface.ipAddress, this.server.type, this.server.location, this.server.system, this.server.manufacturer];
		this.advancementPercent = (values.filter(v => !!v).length / values.length) * 100;

		this.ipAddressControl.setValue(this.getServerPrimaryInterface(this.server)?.serverInterface.ipAddress || null, {emitEvent: false});
		const type: ServerType = this.typeList.find(c => c === this.server.type)!;

		this.typeControl.setValue(type, {emitEvent: false});
		const system: ServerOS = this.systemList.find(c => c === this.server.system)!;

		this.systemControl.setValue(system, {emitEvent: false});
		this.manufacturerControl.setValue(this.server.manufacturer, {emitEvent: false});

		if (this.server.automatic) {
			this.systemControl.disable();
			this.ipAddressControl.disable();
			this.manufacturerControl.disable();
		}
	}

	createLocation(location: string): void {
		this.locationControl.setValue(location);
	}

	updateServerInformation(): void {
		this.switchSaving()
			.pipe(
				filter(() => this.hasInformationChanged() && this.isFormValid()),
				map(() => this.buildServerForm()),
				switchMap(form => this.serverService.updateServerInformation(this.tenantId, this.server.id, form)),
				tap((updateSucceeded) => this.displayUpdatedSucceed(updateSucceeded)),
				tap(() => this.serverDetailService.refreshServer()),
				takeWhile(() => !this.subscription.closed),
				finalize(() => this.switchSaving()))
			.subscribe();
	}

	updateServerInterface(): void {
		const existingInterface = this.server.interfaces.find(i => i.serverInterface.isPrimary)?.serverInterface
		const ipAddress = this.ipAddressControl.value;

		if (existingInterface) {
			if (ipAddress) {
				const form: ServerInterfaceNetworkForm = {
					previousIpAddress: existingInterface.ipAddress,
					ipAddress: ipAddress,
					isPrimary: true,
					networkId: existingInterface.networkId
				}
				this.switchLoading()
					.pipe(
						switchMap(() => this.serverService.createOrUpdateServerInterface(this.tenantId, this.server.id, form)),
						filter(success => success),
						tap(() => this.snackBarService.show(this.translateService.instant('page.application.detail.update.success'))),
						finalize(() => this.switchLoading())
					).subscribe(() => this.serverDetailService.refreshServer());
			} else {
				this.snackBarService.show(this.translateService.instant('page.infrastructure.server.ipAddressRequired'), "danger-snack")
			}
		} else {
			if (ipAddress) {
				const form: ServerInterfaceNetworkForm = {
					previousIpAddress: ipAddress,
					ipAddress: ipAddress,
					isPrimary: true,
					networkId: null
				}
				this.switchLoading()
					.pipe(
						switchMap(() => this.serverService.createOrUpdateServerInterface(this.tenantId, this.server.id, form)),
						filter(success => success),
						tap(() => this.snackBarService.show(this.translateService.instant('page.application.detail.update.success'))),
						finalize(() => this.switchLoading())
					).subscribe(() => this.serverDetailService.refreshServer());
			}
		}
	}

	hasInformationChanged(): boolean {
		return (this.getServerPrimaryInterface(this.server)?.serverInterface.ipAddress || null) !== (this.ipAddressControl.value || null)
			|| this.server.location !== (this.locationControl.value || null)
			|| this.server.type !== (this.typeControl.value || null)
			|| this.server.system !== (this.systemControl.value || null)
			|| this.server.manufacturer !== (this.manufacturerControl.value || null);
	}

	isFormValid(): boolean {
		if (this.server.automatic) {
			return this.locationControl.valid && this.typeControl.valid;
		} else {
			return this.ipAddressControl.valid
				&& this.locationControl.valid
				&& this.typeControl.valid
				&& this.systemControl.valid
				&& this.manufacturerControl.valid;
		}
	}

	isIpInNetworkRange(serverInterface?: ServerInterfaceNetwork): boolean {
		if (serverInterface && serverInterface.network && this.ipAddressControl.value)
			return this.ipCheckerService.isIpInRange(this.ipAddressControl.value, serverInterface.network.ipAddress, serverInterface.network.subnet);
		else
			return true;
	}

	buildServerForm(): ServerInformationForm {
		return {
			name: this.server.name,
			ipAddress: !this.server.automatic ? this.ipAddressControl.value || undefined : undefined,
			location: this.locationControl.value || undefined,
			type: this.typeControl.value,
			system: !this.server.automatic ? this.systemControl.value || undefined : undefined,
			manufacturer: !this.server.automatic ? this.manufacturerControl.value || undefined : undefined
		};
	}

	displayUpdatedSucceed(updateSucceed: boolean): void {
		if (updateSucceed) {
			this.snackBarService.show(this.translateService.instant('page.application.detail.update.success'));
		} else {
			this.snackBarService.show(this.translateService.instant('page.application.detail.update.fail'), 'danger-snack');
		}
	}

	switchLoading(): Observable<{}> {
		this._loading = !this._loading;
		return of({});
	}

	switchSaving(): Observable<{}> {
		this._saving = !this._saving;
		return of({});
	}

	getServerPrimaryInterface(server: Server): ServerInterfaceNetwork | undefined {
		return server.interfaces.find(i => i.serverInterface.isPrimary);
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	protected readonly Array = Array;
	protected readonly isNetworkConfigurationValid = isNetworkConfigurationValid;
}
